import { Button, Popover, PopoverContent, PopoverTrigger } from '@playcastdotio/Shadcn';
import { Link } from 'react-router-dom';
import { download } from '../../utility/api';
import { useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faWindows } from '@fortawesome/free-brands-svg-icons';
import config from '../../config.json';
import { faCircleUser, faComputer, faFingerprint, faLock, faUserSecret } from '@fortawesome/pro-solid-svg-icons';
import { SignedIn, SignedOut, SignInButton, UserButton } from '@clerk/clerk-react';
import { authStateStore } from '../api/stores';
import { DotIcon, User } from 'lucide-react';
import { faUserCog } from '@fortawesome/pro-solid-svg-icons';
import { Privacy } from '@playcastdotio/Settings';

type ProfileNavProps = {
  name: string;
  handler?: () => void;
  url?: string;
};

export const Header = () => {
  // const { getAccessTokenSilently, isAuthenticated, getIdTokenClaims, user } = useAuth0();

  const links = [
    {
      name: 'Discord',
      url: '/discord',
    },
    {
      name: 'News',
      url: '/news',
    },
    {
      name: 'About',
      url: '/about-us',
    },
    {
      name: 'FAQs',
      url: '/faqs',
    },
  ];

  useEffect(() => {
    const links = document.querySelectorAll('#mobile-menu-links a');
    const checkbox = document.querySelector('#mobile-menu') as HTMLInputElement;

    links.forEach((link) => {
      link.addEventListener('click', () => {
        if (checkbox) {
          checkbox.checked = false;
        }
      });
    });

    return () => {
      links.forEach((link) => {
        link.removeEventListener('click', () => {
          checkbox.checked = false;
        });
      });
    };
  }, []);

  // useEffect(() => {
  //   const fetchTokens = async () => {
  //     try {
  //       const token = await getAccessTokenSilently();
  //       const claims = await getIdTokenClaims();
  //       const id_token = claims?.__raw;
  //       // const idToken = user?.id_token;
  //       authStateStore.updateState({
  //         idtoken: id_token || '',
  //         accessToken: token,
  //         email: user?.email || '',
  //         playerAuthId: user?.sub || '',
  //       });
  //     } catch (error) {
  //       console.error('Error fetching tokens:', error);
  //     }
  //   };

  //   if (isAuthenticated) {
  //     console.log('Authenticated:', user);
  //     fetchTokens();
  //   }
  // }, [getAccessTokenSilently, getIdTokenClaims, isAuthenticated, user]);

  return (
    <>
      {/* Desktop header */}
      <header className='bg-header text-content-text sticky top-0 z-10 m-0 hidden w-full border-b-2 border-purple-900 px-4 pt-2 align-middle md:flex'>
        <div id='logo' className='mr-2 flex flex-row gap-x-2 p-2 align-middle lg:mr-16'>
          <img src='/images/playcast_logo-96x96.png' alt='Playcast interactive game streaming platform banner' className='h-12 w-12' />
          <h1 className='text-content-text-headline font-headline text-3xl font-bold md:pt-2'>
            <Link to='/'>Playcast</Link>
          </h1>
        </div>

        <nav id='large menu' className='flex flex-grow gap-x-4 p-2 align-middle'>
          <ul className='flex h-min flex-row align-middle md:gap-x-4 md:pt-3.5 md:text-lg lg:gap-x-8 lg:pt-3 lg:text-xl xl:gap-x-10'>
            {links.map((link) => (
              <li key={link.name} className='hover:text-primary-hover focus:text-primary-hover active:text-primary-active'>
                <Link to={link.url}>{link.name}</Link>
              </li>
            ))}
          </ul>
          <div className='ml-auto mr-8 flex gap-x-4'>
            <Button className='flex space-x-2 rounded-full' onClick={download}>
              <FontAwesomeIcon icon={faWindows} className='text-2xl' />
              <span>Download</span>
            </Button>
            <SignedOut>
              <SignInButton mode='modal'>
                {/* Custom button content */}
                <button className='p-2 text-gray-300 hover:text-white'>
                  <User size={24} />
                </button>
              </SignInButton>
            </SignedOut>
            <SignedIn>
              <UserButton
                appearance={{
                  elements: {
                    rootBox: 'items-center',
                    userButtonTrigger: 'h-8 focus:!h-8',
                    userButtonAvatarBox: 'size-8',
                  },
                }}
              >
                <UserButton.MenuItems>
                  <UserButton.Link
                    label='Remote Dashboard'
                    labelIcon={<FontAwesomeIcon icon={faComputer} size='sm' />}
                    href={config.DASHBOARD_URL}
                  />
                </UserButton.MenuItems>
                <UserButton.UserProfilePage
                  label='Privacy'
                  url='privacy'
                  labelIcon={
                    <div>
                      <FontAwesomeIcon icon={faLock} className='text-md' />
                    </div>
                  }
                >
                  <div className='h-100 flex flex-col gap-y-2'>
                    <Privacy />
                  </div>
                </UserButton.UserProfilePage>
              </UserButton>
            </SignedIn>
          </div>
        </nav>
      </header>

      {/* Mobile header */}
      <header className='bg-header text-content-text sticky top-0 z-10 m-0 flex w-full border-b-2 border-purple-900 px-4 pt-2 align-middle md:hidden'>
        <nav id='mobile menu' className='flex justify-start gap-x-2 p-2 align-middle'>
          <label className='relative z-40 cursor-pointer px-3 py-6' htmlFor='mobile-menu'>
            <input className='peer hidden' type='checkbox' id='mobile-menu' />
            <div className="relative z-50 block h-[1px] w-7 bg-transparent bg-white content-[''] before:absolute before:top-[-0.35rem] before:z-50 before:block before:h-full before:w-full before:bg-white before:transition-all before:duration-200 before:ease-out before:content-[''] after:absolute after:bottom-[-0.35rem] after:left-0 after:block after:h-full after:w-full after:bg-white after:transition-all after:duration-200 after:ease-out after:content-[''] peer-checked:bg-transparent before:peer-checked:top-0 before:peer-checked:w-full before:peer-checked:rotate-45 before:peer-checked:transform after:peer-checked:bottom-0 after:peer-checked:w-full after:peer-checked:-rotate-45 after:peer-checked:transform"></div>
            <div className='fixed inset-0 z-40 hidden h-full w-full bg-black/50 backdrop-blur-sm peer-checked:block'>&nbsp;</div>
            <div className='fixed left-0 top-0 z-40 h-full w-full -translate-x-full overflow-y-auto overscroll-y-none transition duration-500 peer-checked:translate-x-0'>
              <div id='mobile-menu-links' className='float-left min-h-full w-[45%] bg-purple-900 px-6 pt-12 shadow-2xl'>
                <menu>
                  <ul className='flex h-min flex-col gap-y-8 py-12 align-middle text-2xl'>
                    {links.map((link) => (
                      <Link key={link.url} to={link.url}>
                        <li className='hover:text-primary-hover focus:text-primary-hover active:text-primary-active flex-row'>
                          {link.name}
                        </li>
                      </Link>
                    ))}
                  </ul>
                </menu>
              </div>
            </div>
          </label>
        </nav>

        <div id='mobile logo' className='flex flex-grow flex-row justify-center gap-x-2 pt-3 align-middle'>
          <img src='/images/playcast_logo-96x96.png' alt='Playcast interactive game streaming platform banner' className='h-10 w-10' />
          <h1 className='text-content-text-headline font-headline pt-1 text-2xl font-bold md:pt-2'>
            <Link to='/'>Playcast</Link>
          </h1>
        </div>

        <div className='flex items-center p-2'>
          <div className='px-3'>
            <SignedOut>
              <SignInButton mode='modal'>
                {/* Custom button content */}
                <button className='p-2 text-gray-300 hover:text-white'>
                  <User size={24} />
                </button>
              </SignInButton>
            </SignedOut>
            <SignedIn>
              <UserButton
                appearance={{
                  elements: {
                    userButtonTrigger: 'h-10 focus:!h-10',
                    userButtonAvatarBox: 'size-7',
                  },
                }}
              >
                <UserButton.MenuItems>
                  <UserButton.Link
                    label='Remote Dashboard'
                    labelIcon={<FontAwesomeIcon icon={faComputer} className='text-md' />}
                    href={config.DASHBOARD_URL}
                  />
                </UserButton.MenuItems>
                <UserButton.UserProfilePage
                  label='Privacy'
                  url='privacy'
                  labelIcon={
                    <div>
                      <FontAwesomeIcon icon={faUserCog} className='text-2xl' />
                    </div>
                  }
                >
                  <div className='flex flex-col gap-y-2'>
                    <Privacy />
                  </div>
                </UserButton.UserProfilePage>
              </UserButton>
            </SignedIn>
          </div>
        </div>
      </header>
    </>
  );
};

const ProfileNav = () => {
  // Host url is current base url + /dashboard
  const hostUrl = window.location.origin + '/dashboard';
  const [profileNav, setProfileNav] = useState<ProfileNavProps[]>([]);

  // useEffect(() => {
  //   if (isAuthenticated) {
  //     const handleLogout = () => {
  //       logout({
  //         logoutParams: {
  //           returnTo: window.location.origin,
  //         },
  //       });
  //     };

  //     setProfileNav([
  //       {
  //         name: 'Remote Host',
  //         url: hostUrl,
  //       },
  //       {
  //         name: 'Settings',
  //         url: '/settings',
  //       },
  //       {
  //         name: 'Logout',
  //         handler: handleLogout,
  //       },
  //     ]);
  //   } else {
  //     setProfileNav([
  //       {
  //         name: 'Login',
  //         handler: loginWithRedirect,
  //       },
  //     ]);
  //   }
  // }, [hostUrl, isAuthenticated, loginWithRedirect, logout]);

  return (
    <Popover>
      <PopoverTrigger>
        <FontAwesomeIcon icon={faCircleUser} className='size-9' />
      </PopoverTrigger>
      <PopoverContent align='end' className='mt-1.5 w-fit rounded-none rounded-b-lg bg-gray-300 px-1 py-2'>
        <nav className='flex flex-col'>
          {profileNav.map((link) => (
            <div key={link.name + '-' + link.url}>
              {link.handler ? (
                <button
                  key={link.url}
                  className='w-full rounded-md border-2 border-gray-300 px-2 py-1 text-left font-[Syne] hover:border-dashed hover:border-purple-500'
                  onClick={link.handler}
                >
                  {link.name}
                </button>
              ) : (
                <Link
                  key={link.name}
                  to={link.url ? link.url : '/'}
                  className='flex flex-1 rounded-md border-2 border-gray-300 px-2 py-1 font-[Syne] hover:border-dashed hover:border-purple-500'
                >
                  {link.name}
                </Link>
              )}
            </div>
          ))}
        </nav>
      </PopoverContent>
    </Popover>
  );
};

